import { useEffect, useMemo, useState } from 'react'
import { useQuery } from 'react-query'

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
} from '@mui/material'
import Typography from '@mui/material/Typography'

import { Trail } from '../../models/Trail.model'
import { weatherResistanceBreakpoints } from '../../models/WeatherResistanceBreakpoints.model'
import { TrailStatusService, TrailStatusSimulateError } from '../../services/TrailStatusService/TrailStatusService'
import { ConfigService } from '../../services/ConfigService/ConfigService'
import { SnackbarContext } from '../../providers/SnackbarContext'
import { StatusEnum, StatusLabels, StatusEnumType } from '../../models/TrailStatusEnum.model'
import Loading from '../Loading/Loading.component'
import { hasEffectiveOverrides } from './AlgoVisualizer.helpers'

type AlgoVisualizerProps = {
  trail: Trail
}

type SimulatorOverrides = {
  weatherResistance: number
  saturationModifier: number
  dryTimeModifier: number | null
  precipitationLookbackDays: number | null
  precipitationDecayed: number
  // Saturation-adjusted decayed precip (server-computed): the value compared against the band.
  precipitationDecayedAdjusted: number
  precipitationLastSevenDays: number
}

const DEFAULT_SATURATION_MODIFIER = 1
const DEFAULT_WEATHER_RESISTANCE = 5
const FALLBACK_LOOKBACK_DAYS = 7

const STATUS_COLOR: Record<StatusEnumType, 'error' | 'warning' | 'success' | 'inherit'> = {
  [StatusEnum.HighImpact]: 'error',
  [StatusEnum.MediumImpact]: 'warning',
  [StatusEnum.LowImpact]: 'success',
  [StatusEnum.Closed]: 'inherit',
  [StatusEnum.Unknown]: 'inherit',
}

// Build the parenthetical diagnostic suffix for the baseline failure alert, e.g. " (500: ...)".
// Admin-only surface, so exposing the backend status/message is intentional and safe here.
const formatBaselineErrorDetail = (error: unknown): string => {
  if (!(error instanceof TrailStatusSimulateError)) {
    return ''
  }
  const { httpStatus, message } = error
  if (httpStatus !== undefined && message) {
    return ` (${httpStatus}: ${message})`
  }
  if (httpStatus !== undefined) {
    return ` (${httpStatus})`
  }
  if (message) {
    return ` (${message})`
  }
  return ''
}

const sanitizeNumber = (raw: string, fallback = 0) => {
  if (raw === '' || raw === '-' || raw === null || raw === undefined) {
    return fallback
  }
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : fallback
}

const AlgoVisualizer = (props: AlgoVisualizerProps) => {
  const { trail } = props

  const { data: regionConfig } = useQuery(['ConfigService.get'], () => new ConfigService().get())

  // Static fallback used only until the baseline call returns (or if it fails). The `trail/get`
  // endpoint that loads this trail does not include `status`, so we cannot derive precipitation
  // from it; we ask the cached baseline endpoint for the production status/inputs instead.
  const fallbackBaseline = useMemo<SimulatorOverrides>(() => {
    const fallbackLookback =
      trail.precipitationLookbackDays ??
      trail.system?.precipitationLookbackDays ??
      (regionConfig?.precipitationLookbackDays
        ? Number(regionConfig.precipitationLookbackDays)
        : FALLBACK_LOOKBACK_DAYS)
    return {
      weatherResistance: trail.weatherResistance ?? DEFAULT_WEATHER_RESISTANCE,
      saturationModifier: trail.saturationModifier ?? trail.system?.saturationModifier ?? DEFAULT_SATURATION_MODIFIER,
      dryTimeModifier: trail.dryTimeModifier ?? trail.system?.dryTimeModifier ?? null,
      precipitationLookbackDays: fallbackLookback,
      precipitationDecayed: 0,
      precipitationDecayedAdjusted: 0,
      precipitationLastSevenDays: 0,
    }
  }, [trail, regionConfig])

  // Fetch the production baseline from the cached baseline endpoint (not the simulate endpoint).
  // This returns the most recent persisted production status plus the inputs it used
  // (weatherResistance / saturationModifier / dryTimeModifier / precipitationLookbackDays) and the
  // precipitation values for the "Production" card. Because it serves a cached live status, the
  // card and override defaults populate for normal live trails even if a fresh simulate would
  // fail — the fallback banner is reserved for when this cached baseline itself is unavailable.
  const {
    data: baselineResult,
    isLoading: isBaselineLoading,
    isError: isBaselineError,
    error: baselineError,
  } = useQuery(['TrailStatusService.getBaseline', trail.id], () => TrailStatusService.getBaseline(trail.id), {
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })

  const baseline = useMemo<SimulatorOverrides>(() => {
    const inputs = baselineResult?.baselineInputs
    if (!inputs) {
      return fallbackBaseline
    }
    return {
      weatherResistance: inputs.weatherResistance ?? fallbackBaseline.weatherResistance,
      saturationModifier: inputs.saturationModifier ?? fallbackBaseline.saturationModifier,
      dryTimeModifier: inputs.dryTimeModifier ?? fallbackBaseline.dryTimeModifier,
      precipitationLookbackDays: inputs.precipitationLookbackDays ?? fallbackBaseline.precipitationLookbackDays,
      precipitationDecayed: inputs.precipitationDecayed ?? baselineResult?.precipitationDecayed ?? 0,
      // Fall back to the raw decayed value when the backend predates the adjusted field.
      precipitationDecayedAdjusted:
        inputs.precipitationDecayedAdjusted ?? inputs.precipitationDecayed ?? baselineResult?.precipitationDecayed ?? 0,
      precipitationLastSevenDays: inputs.precipitationLastSevenDays ?? baselineResult?.precipitationLastSevenDays ?? 0,
    }
  }, [baselineResult, fallbackBaseline])

  const baselineStatusFromSim: StatusEnumType = (baselineResult?.status ?? StatusEnum.Unknown) as StatusEnumType

  const [overrides, setOverrides] = useState<SimulatorOverrides>(baseline)
  const [simulatedStatus, setSimulatedStatus] = useState<StatusEnumType>(baselineStatusFromSim)
  const [isLoading, setIsLoading] = useState(false)
  // Raw text for the Saturation Modifier input so it can be cleared / typed freely (including 0
  // and partial decimals) rather than snapping back to the default on every keystroke.
  const [saturationText, setSaturationText] = useState(() => String(baseline.saturationModifier))

  // Clobber overrides whenever the baseline changes (different trail or refreshed baseline).
  // Dirty-guarding is intentionally skipped per product guidance.
  useEffect(() => {
    setOverrides(baseline)
    setSimulatedStatus(baselineStatusFromSim)
    setSaturationText(String(baseline.saturationModifier))
  }, [baseline, baselineStatusFromSim, trail.id])

  const patchOverrides = (patch: Partial<SimulatorOverrides>) => {
    setOverrides((prev) => ({ ...prev, ...patch }))
  }

  // Runs the admin simulation endpoint with the current override values (optionally taking a
  // fresh lookback so callers don't have to wait for React state to flush) and copies the
  // recomputed precipitation + resulting status into the simulation card. All editable inputs
  // (weather resistance, saturation modifier, dry-time modifier, lookback days) are forwarded
  // because the backend's decay math depends on dry-time modifier — changing dry-time must
  // trigger a re-simulation, not just a local status lookup. Individual fields that match baseline
  // are omitted from the payload; if *all* of them match, we skip the call entirely (below).
  const runSimulation = async (lookbackOverride?: number | null) => {
    const lookback = lookbackOverride === undefined ? overrides.precipitationLookbackDays : lookbackOverride

    // With no effective overrides, a fresh `/simulate` would recompute against current weather and
    // can diverge from the cached production baseline. Mirror the baseline instead so returning a
    // field (e.g. lookback) to its baseline value restores the production reading rather than
    // showing a fresh recompute.
    if (!hasEffectiveOverrides(overrides, baseline, lookback)) {
      patchOverrides({
        precipitationDecayed: baseline.precipitationDecayed,
        precipitationDecayedAdjusted: baseline.precipitationDecayedAdjusted,
        precipitationLastSevenDays: baseline.precipitationLastSevenDays,
      })
      setSimulatedStatus(baselineStatusFromSim)
      return
    }

    const payload: Parameters<typeof TrailStatusService.simulate>[0] = { trailId: trail.id }
    if (overrides.weatherResistance !== baseline.weatherResistance) {
      payload.weatherResistance = overrides.weatherResistance
    }
    if (overrides.saturationModifier !== baseline.saturationModifier) {
      payload.saturationModifier = overrides.saturationModifier
    }
    if (overrides.dryTimeModifier !== baseline.dryTimeModifier) {
      payload.dryTimeModifier = overrides.dryTimeModifier
    }
    if (lookback !== baseline.precipitationLookbackDays) {
      payload.precipitationLookbackDays = lookback
    }
    try {
      setIsLoading(true)
      const result = await TrailStatusService.simulate(payload)
      patchOverrides({
        precipitationDecayed: result.precipitationDecayed,
        precipitationDecayedAdjusted:
          result.simulationInputs?.precipitationDecayedAdjusted ?? result.precipitationDecayed,
        precipitationLastSevenDays: result.precipitationLastSevenDays,
      })
      setSimulatedStatus(result.status)
    } catch (err) {
      SnackbarContext.show(`Failed to run simulation${formatBaselineErrorDetail(err)}`, 'error')
    } finally {
      setIsLoading(false)
    }
  }

  const handleLookbackChange = async (rawValue: string) => {
    const days = sanitizeNumber(rawValue, baseline.precipitationLookbackDays ?? FALLBACK_LOOKBACK_DAYS)
    patchOverrides({ precipitationLookbackDays: days })
    await runSimulation(days)
  }

  const handleSubmit = async () => {
    await runSimulation()
  }

  const keyDownSubmit = async (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      await handleSubmit()
    }
  }

  const resetOverrides = () => {
    setOverrides(baseline)
    setSimulatedStatus(baselineStatusFromSim)
    setSaturationText(String(baseline.saturationModifier))
  }

  const baselineStatus: StatusEnumType = baselineStatusFromSim

  // The baseline endpoint reports whether the production status came from cache
  // (`cached-inputs`/`cached-derived`) or had to be freshly computed (`fresh-compute`); the latter
  // means we cannot guarantee a same-snapshot comparison, so we surface a warning.
  const noCachedSnapshot = baselineResult?.source === 'fresh-compute'

  return (
    <Paper sx={{ py: { zero: 2, sm: 4 }, px: 2 }}>
      {isBaselineLoading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading current conditions for this trail&hellip;
        </Alert>
      )}
      {isBaselineError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Could not load the cached production baseline{formatBaselineErrorDetail(baselineError)}. Showing simulator
          defaults derived from trail/system/region settings.
        </Alert>
      )}

      <Stack direction={{ zero: 'column', md: 'row' }} justifyContent="space-between" alignItems="stretch" spacing={3}>
        <Stack spacing={2} flex={1}>
          <Typography variant="subtitle1" component="h4">
            Overrides
          </Typography>

          <FormControl variant="outlined" fullWidth>
            <InputLabel id="algo-visualizer-weather-resistance-select-label">Weather Resistance</InputLabel>
            <Select
              labelId="algo-visualizer-weather-resistance-select-label"
              id="algo-visualizer-weather-resistance-select"
              label="Weather Resistance"
              value={overrides.weatherResistance}
              onChange={(event) => patchOverrides({ weatherResistance: Number(event.target.value) })}
            >
              {weatherResistanceBreakpoints.map((eachBreakpoint, index) => (
                <MenuItem key={`${eachBreakpoint[0]}_${eachBreakpoint[1]}_${index}`} value={index + 1}>
                  {index + 1} - (
                  {index < 18
                    ? ` ${eachBreakpoint[0]} mm to ${eachBreakpoint[1]} mm `
                    : {
                        18: ' 50 mm Medium Impact but never High Impact ',
                        19: ' Always Low Impact ',
                      }[index]}
                  )
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant="outlined" fullWidth>
            <InputLabel id="algo-visualizer-saturation-modifier-label">Saturation Modifier</InputLabel>
            <OutlinedInput
              id="algo-visualizer-saturation-modifier"
              label="Saturation Modifier"
              value={saturationText}
              type="number"
              onChange={(e) => {
                const raw = e.target.value
                setSaturationText(raw)
                // Commit only a valid number; leave the override untouched while the field is
                // empty or mid-decimal so typing/clearing never snaps back to the default.
                if (raw !== '' && Number.isFinite(Number(raw))) {
                  patchOverrides({ saturationModifier: Number(raw) })
                }
              }}
              onBlur={() => {
                if (saturationText === '' || !Number.isFinite(Number(saturationText))) {
                  setSaturationText(String(overrides.saturationModifier))
                }
              }}
              onKeyDown={keyDownSubmit}
            />
          </FormControl>

          <FormControl variant="outlined" fullWidth>
            <InputLabel id="algo-visualizer-dry-time-modifier-label">Dry-Time Modifier</InputLabel>
            <OutlinedInput
              id="algo-visualizer-dry-time-modifier"
              label="Dry-Time Modifier"
              value={overrides.dryTimeModifier ?? ''}
              type="number"
              placeholder="(inherit)"
              onChange={(e) =>
                patchOverrides({
                  dryTimeModifier: e.target.value === '' ? null : sanitizeNumber(e.target.value, 0),
                })
              }
              onKeyDown={keyDownSubmit}
            />
          </FormControl>

          <FormControl variant="outlined" fullWidth>
            <InputLabel id="algo-visualizer-lookback-days-label">Lookback Window (days)</InputLabel>
            <OutlinedInput
              id="algo-visualizer-lookback-days"
              label="Lookback Window (days)"
              value={overrides.precipitationLookbackDays ?? ''}
              type="number"
              onChange={(e) => {
                const days = e.target.value === '' ? null : sanitizeNumber(e.target.value, FALLBACK_LOOKBACK_DAYS)
                patchOverrides({ precipitationLookbackDays: days })
              }}
              onBlur={(e) => handleLookbackChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleLookbackChange((e.target as HTMLInputElement).value)
                }
              }}
            />
          </FormControl>

          <Divider />

          <Typography variant="caption" color="grey.400">
            Computed from current weather:
          </Typography>

          <FormControl variant="outlined" fullWidth>
            <InputLabel id="algo-visualizer-decayed-precipitation-label">Adjusted Decayed Precipitation</InputLabel>
            <OutlinedInput
              id="algo-visualizer-decayed-precipitation"
              label="Adjusted Decayed Precipitation"
              value={overrides.precipitationDecayedAdjusted.toFixed(2)}
              readOnly
            />
          </FormControl>

          <FormControl variant="outlined" fullWidth>
            <InputLabel id="algo-visualizer-lookback-precipitation-label">Lookback Precipitation</InputLabel>
            <OutlinedInput
              id="algo-visualizer-lookback-precipitation"
              label="Lookback Precipitation"
              value={overrides.precipitationLastSevenDays.toFixed(2)}
              readOnly
            />
          </FormControl>

          <Stack direction="row" spacing={1}>
            <Button variant="contained" color="primary" onClick={handleSubmit} disabled={isLoading}>
              Run Simulation
            </Button>
            <Button variant="outlined" onClick={resetOverrides} disabled={isLoading}>
              Reset
            </Button>
          </Stack>
        </Stack>

        <Stack flex={1} spacing={2}>
          <Typography variant="subtitle1" component="h4">
            Production vs. Simulation
          </Typography>

          {/* stacked on phones, where two cards side by side don't fit */}
          <Stack direction={{ zero: 'column', sm: 'row' }} spacing={2} alignItems="stretch">
            <StatusCard
              title="Production"
              trailName={trail.name}
              status={baselineStatus}
              precipitationDecayed={baseline.precipitationDecayed}
              precipitationDecayedAdjusted={baseline.precipitationDecayedAdjusted}
              precipitationLastSevenDays={baseline.precipitationLastSevenDays}
              lookbackDays={baseline.precipitationLookbackDays ?? FALLBACK_LOOKBACK_DAYS}
            />
            <StatusCard
              title="Simulation"
              trailName={trail.name}
              status={simulatedStatus}
              precipitationDecayed={overrides.precipitationDecayed}
              precipitationDecayedAdjusted={overrides.precipitationDecayedAdjusted}
              precipitationLastSevenDays={overrides.precipitationLastSevenDays}
              lookbackDays={overrides.precipitationLookbackDays ?? FALLBACK_LOOKBACK_DAYS}
            />
          </Stack>

          {isLoading && <Loading sx={{ height: 80 }} />}

          {noCachedSnapshot && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              No cached production snapshot is available for this trail, so the production card was computed fresh.
              Simulation results are not anchored to a stored live status and may not line up exactly with what users
              currently see.
            </Alert>
          )}
        </Stack>
      </Stack>
    </Paper>
  )
}

type StatusCardProps = {
  title: string
  trailName: string
  status: StatusEnumType
  precipitationDecayed: number
  precipitationDecayedAdjusted: number
  precipitationLastSevenDays: number
  lookbackDays: number
}

const StatusCard = ({
  title,
  trailName,
  status,
  precipitationDecayed,
  precipitationDecayedAdjusted,
  precipitationLastSevenDays,
  lookbackDays,
}: StatusCardProps) => {
  const color = STATUS_COLOR[status] ?? 'inherit'
  // Saturation contribution = adjusted - raw. Dry-time + lookback window move the raw line;
  // the saturation modifier moves this gap. The adjusted value is what's compared to the band.
  const saturationContribution = precipitationDecayedAdjusted - precipitationDecayed
  return (
    <Box
      sx={{
        flex: 1,
        borderRadius: 1,
        background: '#404040',
        p: 2,
        minWidth: 0,
        color: 'white',
      }}
    >
      <Typography variant="overline" component="div" sx={{ color: 'white' }}>
        {title}
      </Typography>
      <Typography variant="body2" component="div" sx={{ color: 'white', mt: 1 }}>
        {trailName}
      </Typography>
      <Stack direction="row" justifyContent="center" sx={{ my: 2 }}>
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <CircularProgress
            variant="determinate"
            value={100}
            size={110}
            color={color === 'inherit' ? 'inherit' : color}
          />
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="h6"
              align="center"
              sx={{ textAlign: 'center', fontSize: '0.95rem', fontWeight: 700, color: 'white', px: 1 }}
            >
              {StatusLabels[status]}
            </Typography>
          </Box>
        </Box>
      </Stack>
      <Typography variant="body2" sx={{ color: 'white' }}>
        Raw decayed: {precipitationDecayed.toFixed(2)} mm
      </Typography>
      <Typography variant="body2" sx={{ color: 'white' }}>
        + saturation: {saturationContribution.toFixed(2)} mm
      </Typography>
      <Typography variant="body2" sx={{ color: 'white', fontWeight: 700 }}>
        Adjusted decayed: {precipitationDecayedAdjusted.toFixed(2)} mm
      </Typography>
      <Typography variant="body2" sx={{ color: 'white', mt: 0.5 }}>
        Lookback precip ({lookbackDays}d): {precipitationLastSevenDays.toFixed(2)} mm
      </Typography>
    </Box>
  )
}

export default AlgoVisualizer
